import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Grid, IconButton, Typography } from '@mui/material';
import { convertsCentsToCurrency, convertsCurrencyToCents } from '@npi/buddy-utils';
import { useField } from 'formik';

import { CustomInput } from 'src/components/CustomInput';
import { DropDown } from 'src/components/FormikComponents';
import { useProductOrderContext } from 'src/contexts/ProductOrderProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';
import { ProductOrderItem } from 'src/types/AddMerchant/serviceRequirements';

import { PricingServiceTypes } from '../ServiceType';

interface OrderItemProps {
    order: ProductOrderItem;
    serviceType: PricingServiceTypes;
    contractTerm: number;
    currency?: string;
    removeProduct: (id: number) => void;
    isEditingDisabled?: boolean;
}

const OrderItem: FC<OrderItemProps> = ({
    order,
    currency,
    contractTerm,
    removeProduct,
    isEditingDisabled = false,
}) => {
    const name = `order.${order?.id}`;
    const [{ value: serviceOrder }] = useField({
        name: 'order',
    });
    const { setOrderItemMonthlyPrice, setOrderItemOneOffPrice, setOrderItemQuantity } =
        useProductOrderContext();
    const { totalOrderPrice, setTotalOrderPrice } = useServiceOrderContext();

    const [quantity, setQuantity] = useState(order?.quantity?.toString() ?? '1');
    const [onceOffPrice, setOnceOffPrice] = useState(order?.oneOffPrice?.toString() ?? '0');
    const [monthlyPrice, setMonthlyPrice] = useState(order?.monthlyPrice?.toString() ?? '0');
    const [onceOffPriceOptions, setOnceOffPriceOptions] = useState([]);
    const [monthlyPriceOptions, setMonthlyPriceOptions] = useState([]);

    const initialValuesSet = useRef(false);

    // Get the options from the service order field
    const serviceOrderProduct = useMemo(() => {
        return serviceOrder?.[order.type.toLowerCase()]?.find((product) => {
            return product?.id === order?.id;
        });
    }, [serviceOrder, order]);

    const getTotal = () => {
        const productOrderOneOffPrice = convertsCentsToCurrency(Number(onceOffPrice)) || 0;
        const productOrderMonthlyPrice = convertsCentsToCurrency(Number(monthlyPrice)) || 0;

        const termTotal = Number(contractTerm) * productOrderMonthlyPrice;
        const total = ((productOrderOneOffPrice + termTotal) * Number(quantity)).toFixed(2);

        return Number(total);
    };

    const handleOnceOffPriceChange = (value) => {
        if (value !== undefined) {
            setOnceOffPrice(value);
            setOrderItemOneOffPrice(order.id, Number(value));

            const totalOneOffs = totalOrderPrice?.totalOneOff || [];
            const productIndex = totalOneOffs?.findIndex((product) => {
                return product.id === order.id;
            });

            if (productIndex !== -1) {
                const updatedProductObject = {
                    ...totalOneOffs[productIndex],
                    price: convertsCurrencyToCents(Number(value)),
                };
                totalOneOffs[productIndex] = updatedProductObject;

                setTotalOrderPrice((prev) => {
                    return {
                        ...prev,
                        totalOneOff: totalOneOffs,
                    };
                });
            } else {
                totalOneOffs.push({
                    id: order.id,
                    price: convertsCurrencyToCents(Number(value)),
                    quantity: Number(quantity),
                });
                setTotalOrderPrice((prev) => {
                    return {
                        ...prev,
                        totalOneOff: totalOneOffs,
                    };
                });
            }
        }
    };

    const handleMonthlyPriceChange = (value) => {
        if (value !== undefined) {
            setMonthlyPrice(value);
            setOrderItemMonthlyPrice(order.id, Number(value));

            const totalMonthlys = totalOrderPrice?.totalMonthly || [];
            const productIndex = totalMonthlys?.findIndex((product) => {
                return product.id === order.id;
            });

            if (productIndex !== -1) {
                const updatedProductObject = {
                    ...totalMonthlys[productIndex],
                    price: convertsCurrencyToCents(Number(value)),
                };
                totalMonthlys[productIndex] = updatedProductObject;

                setTotalOrderPrice((prev) => {
                    return {
                        ...prev,
                        totalMonthly: totalMonthlys,
                    };
                });
            } else {
                totalMonthlys.push({
                    id: order.id,
                    price: convertsCurrencyToCents(Number(value)),
                    quantity: Number(quantity),
                });
                setTotalOrderPrice((prev) => {
                    return {
                        ...prev,
                        totalMonthly: totalMonthlys,
                    };
                });
            }
        }
    };

    const handleQuantityChange = (e) => {
        const quantity = Number.isInteger(Number(e.target.value)) ? e.target.value : '1';
        setQuantity(quantity);
        setOrderItemQuantity(order.id, parseInt(quantity));

        const totalOneOffs = totalOrderPrice?.totalOneOff || [];
        const productIndex_oneOffs = totalOneOffs.findIndex((product) => {
            return product.id === order.id;
        });

        const totalMonthlys = totalOrderPrice?.totalMonthly || [];
        const productIndex_monthly = totalMonthlys.findIndex((product) => {
            return product.id === order.id;
        });

        if (productIndex_oneOffs !== -1) {
            const updatedProductObject_oneOff = {
                ...totalOneOffs[productIndex_oneOffs],
                quantity: Number(e.target.value),
            };
            totalOneOffs[productIndex_oneOffs] = updatedProductObject_oneOff;
        }

        if (productIndex_monthly !== -1) {
            const updatedProductObject_monthly = {
                ...totalMonthlys[productIndex_monthly],
                quantity: Number(e.target.value),
            };
            totalMonthlys[productIndex_monthly] = updatedProductObject_monthly;
        }

        setTotalOrderPrice((prev) => {
            return {
                ...prev,
                totalOneOff: totalOneOffs,
                totalMonthly: totalMonthlys,
            };
        });
    };

    useEffect(() => {
        if (!initialValuesSet.current) {
            const productOneOffPrice = totalOrderPrice?.totalOneOff?.find((el) => {
                return el.id === order.id;
            })?.price;
            const productMonthlyPrice = totalOrderPrice?.totalMonthly?.find((el) => {
                return el.id === order.id;
            })?.price;

            const productQuantity =
                totalOrderPrice?.totalOneOff?.find((el) => {
                    return el.id === order.id;
                })?.quantity ||
                totalOrderPrice?.totalMonthly?.find((el) => {
                    return el.id === order.id;
                })?.quantity ||
                order?.quantity;

            if (productOneOffPrice !== undefined && productOneOffPrice !== null) {
                setOnceOffPrice(productOneOffPrice.toString());
            }
            if (productMonthlyPrice !== undefined && productMonthlyPrice !== null) {
                setMonthlyPrice(productMonthlyPrice.toString());
            }
            if (productQuantity !== undefined && productQuantity !== null) {
                setQuantity(productQuantity.toString());
            }

            initialValuesSet.current = true;
        }
        // eslint-disable-next-line
    }, [order.id, totalOrderPrice]);

    useEffect(() => {
        setOnceOffPriceOptions(serviceOrderProduct?.options?.oneOff || []);
        setMonthlyPriceOptions(serviceOrderProduct?.options?.monthly || []);
    }, [serviceOrderProduct]);

    const onceOffDisabled =
        onceOffPriceOptions
            ?.filter((option) => {
                return option?.sales;
            })
            .map((option) => {
                return {
                    value: option?.sales?.toString(),
                    label: option?.sales?.toString(),
                    currency: option?.currency,
                };
            }).length === 0;

    const monthlyDisabled =
        monthlyPriceOptions
            ?.filter((option) => {
                return option?.sales;
            })
            .map((option) => {
                return {
                    value: option?.sales?.toString(),
                    label: option?.sales?.toString(),
                    currency: option?.currency,
                };
            }).length === 0;

    return (
        <>
            {/* Product name */}
            <Grid item xs={3} alignSelf="center">
                <Typography fontSize="13px" color="#1D1D1B" textAlign="start" marginLeft={'16px'}>
                    {serviceOrderProduct?.name}
                </Typography>
            </Grid>

            {/* Setup, QTY, Monthly Rental, Total */}
            <Grid item xs={2} bgcolor="#F9FAFB">
                <DropDown
                    name={`${name}.onceOffprice`}
                    options={onceOffPriceOptions.map((option) => {
                        return {
                            value: option?.sales,
                            label: `${currency}${convertsCentsToCurrency(Number(option?.sales))}`,
                        };
                    })}
                    disabled={onceOffDisabled || isEditingDisabled}
                    onChange={handleOnceOffPriceChange}
                    placeholder={`${currency}${convertsCentsToCurrency(Number(onceOffPrice)) ?? 0}`}
                />
            </Grid>
            <Grid item xs={2} bgcolor="#F9FAFB">
                <CustomInput
                    name={`${name}.quantity`}
                    value={quantity}
                    onChange={handleQuantityChange}
                    alignInput="center"
                    boxStyling={{ height: '100%' }}
                    size="large"
                    disabled={isEditingDisabled}
                    type="number"
                    min={1}
                />
            </Grid>
            <Grid item xs={2} bgcolor="#F9FAFB">
                <DropDown
                    name={`${name}.monthlyPrice`}
                    label=""
                    options={monthlyPriceOptions.map((option) => {
                        return {
                            value: option?.sales,
                            label: `${currency}${convertsCentsToCurrency(Number(option?.sales))}`,
                        };
                    })}
                    disabled={monthlyDisabled || isEditingDisabled}
                    onChange={handleMonthlyPriceChange}
                    placeholder={`${currency}${convertsCentsToCurrency(Number(monthlyPrice)) ?? 0}`}
                />
            </Grid>
            <Grid
                item
                xs={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="#F9FAFB"
            >
                <Typography fontSize="16px" color="#1D1D1B" textAlign="center">
                    {`${currency} ${getTotal()}`}
                </Typography>
            </Grid>

            {/* Clear button */}
            <Grid item xs={1} alignSelf="center">
                <IconButton
                    size="small"
                    onClick={() => {
                        return removeProduct(order.id);
                    }}
                    disabled={isEditingDisabled}
                >
                    <Close
                        sx={{
                            color: '#F39E4E',
                        }}
                    />
                </IconButton>
            </Grid>
        </>
    );
};

export default OrderItem;
